import { useState } from 'react';
import { Building2, DollarSign, CreditCard, Scale, RefreshCw, Combine } from 'lucide-react';
import { useAccounts, useUpdateAccount, useMergeAccounts, useStatements, useDeleteStatement, useDiscoveryLeads, useUpdateAccountParty, useAutoAssignParties, useDuplicateAccounts, useCombineDuplicateAccounts, useMoveStatement, useCoverageReport } from '../../hooks/useStatements';
import type { SourceParty } from '../../hooks/useStatements';
import {
  Button,
  ConfirmModal,
  EmptyState,
  MetricCard,
  Money,
  Skeleton,
  Tabs,
  Tag,
  ToolbarChip,
  ToolbarGroup,
  ToolbarLabel,
} from '../ui';
import type { TabItem, TagTone } from '../ui';
import AccountCard, { getOwnerDisplayName, isCreditCardType } from '../AccountCard';
import MissingAccountsTab from '../MissingAccountsTab';

interface AccountsTabProps {
  caseId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
  onNavigateToTransactions?: (statementId: number) => void;
  onNavigateToAccountTransactions?: (bankLabel: string) => void;
}

type StatementFilter = 'all' | 'needs_review' | 'clean';
// Provenance filter — which side PRODUCED the documents (source_party), the
// case-level "client discovery vs OC discovery" toggle. 'unset' finds
// statements whose production source was never recorded.
type ProductionFilter = 'all' | 'client' | 'opposing' | 'third_party' | 'unset';
type AccountsSubTab = 'accounts' | 'missing';

const OWNER_TONES: Record<string, TagTone> = {
  Client: 'client',
  Opposing: 'opposing',
  Joint: 'joint',
  'Third Party': 'third',
  Unknown: 'neutral',
};

export default function AccountsTab({ caseId, clientName, opposingPartyName, onNavigateToTransactions, onNavigateToAccountTransactions }: AccountsTabProps) {
  const { data: accountsResponse, isLoading, error } = useAccounts(caseId);
  const accounts = accountsResponse?.accounts;
  const successions = accountsResponse?.successions ?? [];
  const { data: allStatements } = useStatements(caseId);
  const { data: discoveryLeads } = useDiscoveryLeads(caseId);
  const { data: coverageReport } = useCoverageReport(caseId);
  const updateAccount = useUpdateAccount(caseId);
  const mergeAccounts = useMergeAccounts(caseId);
  const deleteStatement = useDeleteStatement();
  const updateAccountParty = useUpdateAccountParty(caseId);
  const autoAssign = useAutoAssignParties(caseId);
  const { data: duplicateGroups } = useDuplicateAccounts(caseId);
  const combineDuplicates = useCombineDuplicateAccounts(caseId);
  const moveStatement = useMoveStatement(caseId);

  const [stmtFilter, setStmtFilter] = useState<StatementFilter>('all');
  const [prodFilter, setProdFilter] = useState<ProductionFilter>('all');
  const [subTab, setSubTab] = useState<AccountsSubTab>('accounts');
  const [rescanMsg, setRescanMsg] = useState<string | null>(null);
  const [combineMsg, setCombineMsg] = useState<string | null>(null);
  const [confirmCombineOpen, setConfirmCombineOpen] = useState(false);

  const leadsCount = discoveryLeads?.length ?? 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Skeleton variant="rect" height={96} className="rounded-lg" />
          <Skeleton variant="rect" height={96} className="rounded-lg" />
        </div>
        <div className="space-y-3">
          <Skeleton variant="rect" height={88} className="rounded-lg" />
          <Skeleton variant="rect" height={88} className="rounded-lg" />
          <Skeleton variant="rect" height={88} className="rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-danger">Failed to load accounts</p>
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return (
      <EmptyState
        icon={Building2}
        title="No Accounts Yet"
        description="Upload bank statements to see accounts here."
      />
    );
  }

  // A credit-card "balance" is the amount OWED (a debt), so it must be
  // subtracted, not added. Sum cash accounts and card debt separately, then
  // derive a true net position.
  const cashTotal = accounts
    .filter((a) => !isCreditCardType(a.account_type))
    .reduce((sum, a) => sum + (a.latest_balance || 0), 0);
  const creditCardDebt = accounts
    .filter((a) => isCreditCardType(a.account_type))
    .reduce((sum, a) => sum + (a.latest_balance || 0), 0);
  const netPosition = cashTotal - creditCardDebt;
  const hasCreditCards = accounts.some((a) => isCreditCardType(a.account_type));
  const totalStatements = accounts.reduce((sum, a) => sum + a.statement_count, 0);

  const needsReviewStatementList = (allStatements || []).filter(s => s.status === 'needs_review');
  const needsReviewAccountBankLabels = new Set(needsReviewStatementList.map(s => s.bank_label));
  const needsReviewCount = needsReviewAccountBankLabels.size;

  // Group by account OWNERSHIP (owner_type) — whose account it is. Provenance
  // (source_party: who produced the documents) is shown per-card, not used here.
  const grouped: Record<string, typeof accounts> = {};
  accounts.forEach(a => {
    const key = a.owner_type || 'Unknown';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  });

  const partyOrder = ['Client', 'Opposing', 'Joint', 'Third Party', 'Unknown'];
  const sortedOwners = partyOrder.filter(o => grouped[o]);
  Object.keys(grouped).forEach(o => {
    if (!sortedOwners.includes(o)) sortedOwners.push(o);
  });

  const filterLabels: Record<StatementFilter, string> = {
    all: 'All',
    needs_review: 'Needs Review',
    clean: 'Clean',
  };

  const stmtMatchesProduction = (s: { source_party?: string | null }) =>
    prodFilter === 'all'
      ? true
      : prodFilter === 'unset'
        ? !s.source_party
        : s.source_party === prodFilter;

  const stmtMatchesStatus = (s: { status?: string | null }) =>
    stmtFilter === 'all'
      ? true
      : stmtFilter === 'needs_review'
        ? s.status === 'needs_review'
        : s.status !== 'needs_review';

  const filterAccounts = (accs: typeof accounts) =>
    stmtFilter === 'all' && prodFilter === 'all'
      ? accs
      : accs.filter(a => (allStatements || []).some(
          s => s.bank_label === a.bank_label && stmtMatchesStatus(s) && stmtMatchesProduction(s)
        ));

  const unsetProductionCount = (allStatements || []).filter(s => !s.source_party).length;

  const visibleGroups = sortedOwners
    .map(owner => ({ owner, accounts: filterAccounts(grouped[owner]) }))
    .filter(g => g.accounts.length > 0);
  const singleGroup = visibleGroups.length === 1 ? visibleGroups[0] : null;
  const hasUnassigned = visibleGroups.some(g => g.owner === 'Unknown');

  const dupStmtTotal = (duplicateGroups ?? []).reduce((sum, g) => sum + g.statement_count, 0);

  const renderOwnerPill = (owner: string, count: number) => (
    <Tag tone={OWNER_TONES[owner] ?? 'neutral'} dot className="shrink-0">
      {getOwnerDisplayName(owner, clientName, opposingPartyName)} ({count} account{count !== 1 ? 's' : ''})
    </Tag>
  );

  const autoDetectButton = (
    <div className="flex items-center gap-3 shrink-0">
      {rescanMsg && <Tag tone="good">{rescanMsg}</Tag>}
      <Button
        size="md"
        variant="secondary"
        icon={RefreshCw}
        loading={autoAssign.isPending}
        onClick={() => {
          setRescanMsg(null);
          autoAssign.mutate(undefined, {
            onSuccess: (result) => {
              setRescanMsg(`Auto-assigned ${result.assigned} of ${result.total} accounts`);
              setTimeout(() => setRescanMsg(null), 5000);
            },
          });
        }}
        title="Re-scan statement PDFs to auto-detect which spouse each unassigned account belongs to, based on the account holder name"
      >
        {autoAssign.isPending ? 'Detecting...' : 'Auto-detect owners'}
      </Button>
    </div>
  );

  const subTabItems: TabItem[] = [
    { id: 'accounts', label: 'Accounts' },
    ...(leadsCount > 0
      ? [{ id: 'missing', label: 'Missing Accounts', count: leadsCount, amber: true }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div className={`grid grid-cols-1 gap-4 ${hasCreditCards ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
        {hasCreditCards ? (
          <>
            <MetricCard
              label="Net Position"
              value={<Money value={netPosition} whole />}
              icon={<Scale size={20} />}
              subtitle="Cash minus card debt"
            />
            <MetricCard
              label="Cash on Hand"
              value={<Money value={cashTotal} whole />}
              icon={<DollarSign size={20} />}
              subtitle="Checking, savings & other"
            />
            <MetricCard
              label="Credit Card Debt"
              value={<Money value={creditCardDebt > 0 ? -creditCardDebt : 0} whole />}
              icon={<CreditCard size={20} />}
              subtitle="Amount owed"
            />
            <MetricCard
              label="Accounts"
              value={accounts.length}
              icon={<Building2 size={20} />}
              subtitle={`${totalStatements} statements`}
            />
          </>
        ) : (
          <>
            <MetricCard
              label="Total Balance"
              value={<Money value={cashTotal} whole />}
              icon={<DollarSign size={20} />}
            />
            <MetricCard
              label="Accounts"
              value={accounts.length}
              icon={<Building2 size={20} />}
              subtitle={`${totalStatements} statements`}
            />
          </>
        )}
      </div>

      <Tabs
        items={subTabItems}
        value={subTab}
        onChange={(id) => setSubTab(id as AccountsSubTab)}
        level="secondary"
        ariaLabel="Account views"
      />

      {subTab === 'missing' && discoveryLeads && discoveryLeads.length > 0 ? (
        <MissingAccountsTab leads={discoveryLeads} />
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ToolbarLabel>Show:</ToolbarLabel>
              <ToolbarGroup>
                {(['all', 'needs_review', 'clean'] as StatementFilter[]).map((f) => (
                  <ToolbarChip
                    key={f}
                    selected={stmtFilter === f}
                    onClick={() => setStmtFilter(f)}
                    count={f === 'needs_review' && needsReviewCount > 0 ? needsReviewCount : undefined}
                  >
                    {filterLabels[f]}
                  </ToolbarChip>
                ))}
              </ToolbarGroup>
            </div>
            <div className="flex items-center gap-2">
              <ToolbarLabel title="Which side handed these documents to the firm">Produced by:</ToolbarLabel>
              <ToolbarGroup>
                {([
                  ['all', 'All'],
                  ['client', clientName || 'Client'],
                  ['opposing', opposingPartyName || 'Opposing'],
                  ['third_party', 'Third party'],
                  ['unset', 'Unset'],
                ] as [ProductionFilter, string][])
                  .filter(([f]) => f !== 'unset' || unsetProductionCount > 0)
                  .map(([f, label]) => (
                    <ToolbarChip
                      key={f}
                      selected={prodFilter === f}
                      onClick={() => setProdFilter(f)}
                      count={f === 'unset' ? unsetProductionCount : undefined}
                    >
                      {label}
                    </ToolbarChip>
                  ))}
              </ToolbarGroup>
            </div>
            {singleGroup && renderOwnerPill(singleGroup.owner, singleGroup.accounts.length)}
            {hasUnassigned && autoDetectButton}
            {duplicateGroups && duplicateGroups.length > 0 && (
              <div className="flex items-center gap-3 shrink-0">
                {combineMsg && <Tag tone="good">{combineMsg}</Tag>}
                <Button
                  size="md"
                  variant="secondary"
                  icon={Combine}
                  loading={combineDuplicates.isPending}
                  onClick={() => setConfirmCombineOpen(true)}
                  title="Some accounts share the same account number but were split apart by slightly different bank names. Combine them into one account each."
                >
                  {combineDuplicates.isPending
                    ? 'Combining...'
                    : `Combine ${duplicateGroups.length} duplicate account${duplicateGroups.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            )}
          </div>

          {visibleGroups.map(({ owner, accounts: filteredAccounts }) => (
              <div key={owner}>
                {!singleGroup && (
                  <div className="mb-3">
                    {renderOwnerPill(owner, filteredAccounts.length)}
                  </div>
                )}
                <div className="space-y-3">
                  {filteredAccounts.map(account => {
                    const accountStatements = (allStatements || []).filter(
                      s => s.bank_label === account.bank_label
                    );
                    const filteredStmts = accountStatements.filter(
                      s => stmtMatchesStatus(s) && stmtMatchesProduction(s)
                    );

                    const accountCoverage = coverageReport?.accounts.find(
                      a => a.bank_label === account.bank_label
                    ) ?? null;

                    return (
                      <AccountCard
                        key={account.bank_label}
                        account={account}
                        allAccounts={accounts}
                        statements={filteredStmts}
                        caseId={caseId}
                        coverage={accountCoverage}
                        succession={successions.find(s => s.predecessor_bank_label === account.bank_label) ?? null}
                        onUpdateAccount={(bankLabel, ownerType, accountType, propertyStatus) => {
                          updateAccount.mutate({ bankLabel, ownerType, accountType, propertyStatus });
                        }}
                        onMergeAccounts={(sourceBankLabels, targetBankLabel) => {
                          mergeAccounts.mutate({ sourceBankLabels, targetBankLabel });
                        }}
                        onMoveStatement={(statementId, targetBankLabel) => {
                          moveStatement.mutate({ statementId, targetBankLabel });
                        }}
                        onDeleteStatement={(statementId) => {
                          deleteStatement.mutate(statementId);
                        }}
                        onSetAccountParty={(bankLabel, party) => {
                          updateAccountParty.mutate({ bankLabel, source_party: party as SourceParty });
                        }}
                        onNavigateToTransactions={onNavigateToTransactions}
                        onNavigateToAccountTransactions={onNavigateToAccountTransactions}
                        isUpdating={updateAccount.isPending}
                        isDeletingStatement={deleteStatement.isPending}
                        isMovingStatement={moveStatement.isPending}
                        clientName={clientName}
                        opposingPartyName={opposingPartyName}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

          {(stmtFilter !== 'all' || prodFilter !== 'all') && visibleGroups.length === 0 && (
            <div className="flex items-center justify-center h-32">
              <p className="text-ink-soft text-sm">
                {prodFilter !== 'all'
                  ? 'No statements match this production source.'
                  : stmtFilter === 'needs_review'
                    ? 'No statements need review.'
                    : 'All statements require review.'}
              </p>
            </div>
          )}
        </>
      )}

      {duplicateGroups && duplicateGroups.length > 0 && (
        <ConfirmModal
          open={confirmCombineOpen}
          onClose={() => setConfirmCombineOpen(false)}
          onConfirm={() => {
            setConfirmCombineOpen(false);
            setCombineMsg(null);
            combineDuplicates.mutate(undefined, {
              onSuccess: (result) => {
                setCombineMsg(`Combined ${result.groups_merged} account${result.groups_merged !== 1 ? 's' : ''} (${result.statements_updated} statement${result.statements_updated !== 1 ? 's' : ''} moved)`);
                setTimeout(() => setCombineMsg(null), 6000);
              },
            });
          }}
          title={`Combine ${duplicateGroups.length} duplicate account${duplicateGroups.length !== 1 ? 's' : ''}?`}
          message={`This will move ${dupStmtTotal} statement${dupStmtTotal !== 1 ? 's' : ''} from look-alike accounts (same number, slightly different bank name) into a single account each. You can move any statement back out afterward.`}
          confirmLabel="Combine accounts"
          loading={combineDuplicates.isPending}
        />
      )}
    </div>
  );
}
